import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

// 25 distinct colors for chart segments
const COLORS = [
    '#5e35b1', // purple
    '#7cb342', // green
    '#ef5350', // red
    '#2e4d3f', // dark green
    '#d4a017', // gold
    '#26a69a', // teal
    '#ec407a', // pink
    '#ff7043', // deep orange
    '#ab47bc', // purple
    '#66bb6a', // light green
    '#42a5f5', // blue
    '#ffa726', // orange
    '#8d6e63', // brown
    '#78909c', // blue grey
    '#9ccc65', // lime
    '#7e57c2', // deep purple
    '#26c6da', // cyan
    '#ffca28', // amber
    '#29b6f6', // light blue
    '#5c6bc0', // indigo
    '#ff5722', // red orange
    '#9e9d24', // lime dark
    '#00897b', // teal dark
    '#6d4c41', // brown dark
    '#455a64', // blue grey dark
];

const LegendItem = ({ color, label, value }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
            sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0
            }}
        />
        <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontSize: 13 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13 }}>
            {value}
        </Typography>
    </Stack>
);

const MostReportedCategories = ({ title = "Most Reported Categories" }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                // Get params with organization_id if applicable
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
                
                // Check if user is Super Admin
                const isSuperAdmin = normalizedRoles.includes('super admin');
                
                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head', 'director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {
                    limit: 20
                };
                
                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                // Use different endpoint for Super Admin
                const res = isSuperAdmin 
                    ? await DashboardServices.getDashboardTopCategoriesCountryLevel(params)
                    : await DashboardServices.getDashboardTopCategories(params);
                const payload = res?.data ?? res;
                // Expected: { view: { msg, data: [{category_id, category_name, case_count}] } }
                const dataArray = payload?.view?.data ?? payload?.data ?? payload ?? [];
                
                // Calculate total for percentages
                const total = dataArray.reduce((sum, item) => sum + (Number(item.case_count) || 0), 0);
                
                // Transform data for chart
                const formatted = Array.isArray(dataArray) 
                    ? dataArray.map((item, index) => {
                        const count = Number(item.case_count) || 0;
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return {
                            name: item.category_name ?? item.name ?? `Category ${index + 1}`,
                            value: count,
                            percentage: percentage,
                            category_id: item.category_id,
                            fill: COLORS[index % COLORS.length]
                        };
                    })
                    : [];

                console.log('Most Reported Categories Data:', formatted);
                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load most reported categories data', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

   // if (loading) return <WidgetCard><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard>
            <MainCardTitle>{title}</MainCardTitle>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', gap: 4, mt: 2, alignItems: 'center' }}>
                    {/* Chart */}
                    <Box sx={{ width: 300, height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <PieChart width={280} height={300}>
                            <Pie
                                data={chartData}
                                cx={140}
                                cy={140}
                                innerRadius={90}
                                outerRadius={130}
                                paddingAngle={5}
                                dataKey="value"
                                cornerRadius={10}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                        </PieChart>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ flex: 1, maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                        {chartData.map((item, index) => (
                            <LegendItem
                                key={index}
                                color={item.fill}
                                label={item.name}
                                value={`${item.percentage}%`}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </WidgetCard>
    );
};

export default MostReportedCategories;
